/*!
 * SkyPlus Indexing Queue Component
 * Location: app/src/app/components/skyplus/indexing/indexing-queue.component.ts
 * 
 * Features:
 * - File preview (images, PDFs, text)
 * - Document type selection with dynamic metadata form
 * - Folder selection or creation from field value
 * - Batch upload with Next/Previous navigation
 * - Edit existing document metadata
 */

import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { WfDocumentTypeApiService } from '../wf-runtime/services/wf-document-type-api.service';
import { IndexingService, UploadOptions } from './indexing.service';

import { WfDocumentTypeRegistryService } from './document-type-registry.service';
import { WfInstanceFilingApiService } from '../wf-runtime/services/wf-instance-filing-api.service';

import { 
  DocumentTypeConfig, 
  DocumentField,
  getEnabledDocumentTypes, 
  getDocumentTypeById,
  getFolderCreationFields,
  getSubfolderCreationFields
} from './document-types.config';

interface QueuedFile {
  file: File;
  previewUrl?: SafeResourceUrl;
  previewType: 'image' | 'pdf' | 'text' | 'other';
  metadata: Record<string, any>;
  documentTypeId?: string;
  targetFolderId?: string;
  createFolderFromField?: string;
  createSubfolderFromField?: string;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  uploadedNodeId?: string;
}

interface BreadcrumbItem {
  id: string;
  name: string;
}

@Component({
  selector: 'sp-indexing-queue',
  templateUrl: './indexing-queue.component.html',
  styleUrls: ['./indexing-queue.component.scss']
})
export class IndexingQueueComponent implements OnInit, OnDestroy {
  
  // Input: Pre-selected folder
  @Input() initialFolderId: string = '-root-';
  
  // Input: Files to index (from external source)
  @Input() set inputFiles(files: File[]) {
    if (files && files.length > 0) {
      this.addFilesToQueue(files);
    }
  }
  
  // Input: Edit mode - load existing document
  @Input() editNodeId: string | null = null;
  
  // Output: When indexing is complete
  @Output() indexingComplete = new EventEmitter<{ nodeId: string; folderId: string }[]>();
  
  // Output: When cancelled
  @Output() indexingCancelled = new EventEmitter<void>();
  
  // Queue state
  queuedFiles: QueuedFile[] = [];
  currentIndex = 0;
  
  // Document types
  documentTypes: DocumentTypeConfig[] = [];
  selectedDocumentType: DocumentTypeConfig | null = null;
  
  // Form state
  formValues: Record<string, any> = {};
  formErrors: Record<string, string> = {};
  
  // Folder selection
  showFolderBrowser = false;
  currentBrowseFolderId = '-root-';
  browseFolderContents: { id: string; name: string; path: string }[] = [];
  folderBreadcrumbs: BreadcrumbItem[] = [{ id: '-root-', name: 'Company Home' }];
  selectedTargetFolder: { id: string; name: string; path: string } | null = null;
  
  // Folder creation options
  folderCreationFields: DocumentField[] = [];
  createFolderFromField: string | null = null;

  // SUBFOLDER creation options
  subfolderCreationFields: DocumentField[] = [];
  createSubfolderFromField: string | null = null;
  
  // UI state
  isLoading = false;
  isSaving = false;
  showDocumentTypeDropdown = false;
  currentUsername = '';
  
  // Edit mode
  isEditMode = false;
  editingNode: any = null;
  originFolderId: string | null = null;
  // Edit-mode document preview
  editPreviewUrl: SafeResourceUrl | null = null;
  editPreviewObjectUrl: string | null = null;
  editPreviewType: 'pdf' | 'image' | 'other' = 'other';
  
  private destroy$ = new Subject<void>();
  
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private indexingService: IndexingService,
    private registry: WfDocumentTypeRegistryService,
    private filingApi: WfInstanceFilingApiService
  ,
    private docTypeApi: WfDocumentTypeApiService
  ) {}
  
  ngOnInit(): void {
    // Static set immediately so the UI is never empty.
    this.documentTypes = getEnabledDocumentTypes();
    this.currentUsername = this.indexingService.getCurrentUsername();

    // Edit mode is driven by the route param (/indexing/:nodeId). The optional
    // ?from=<folderId> lets us return to the originating folder on back/cancel.
    const routeNodeId = this.route.snapshot.paramMap.get('nodeId');
    const fromFolder = this.route.snapshot.queryParamMap.get('from');
    if (fromFolder) { this.originFolderId = fromFolder; }
    const editId = routeNodeId || this.editNodeId;

    // Load registry types FIRST, then run edit matching — cm:content types like
    // claim_doc only resolve correctly once registry types (with aspects) are
    // present. afterTypes runs on both resolve and reject so a registry hiccup
    // still loads the node.
    const afterTypes = () => {
      this.documentTypes = getEnabledDocumentTypes();
      if (editId) { this.loadNodeForEdit(editId); }
    };
    this.registry.load().then(afterTypes, afterTypes);

    if (this.initialFolderId && !editId) {
      this.selectTargetFolderById(this.initialFolderId);
    }
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Revoke object URLs
    this.queuedFiles.forEach(qf => {
      if (qf.previewUrl && typeof qf.previewUrl === 'string') {
        URL.revokeObjectURL(qf.previewUrl);
      }
    });
    if (this.editPreviewObjectUrl) { URL.revokeObjectURL(this.editPreviewObjectUrl); }
  }
  
  // ===== FILE QUEUE MANAGEMENT =====
  
  /**
   * Add files to the queue
   */
  addFilesToQueue(files: File[] | FileList): void {
    const fileArray = Array.from(files);
    
    fileArray.forEach(file => {
      const queuedFile: QueuedFile = {
        file,
        previewType: this.getPreviewType(file),
        metadata: {},
        status: 'pending'
      };
      
      // Generate preview URL
      if (queuedFile.previewType !== 'other') {
        const objectUrl = URL.createObjectURL(file);
        queuedFile.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
      }
      
      this.queuedFiles.push(queuedFile);
    });
    
    // If this is the first file, initialize form
    if (this.queuedFiles.length > 0 && this.currentIndex === 0) {
      this.initializeFormForCurrentFile();
    }
  }
  
  /**
   * Determine preview type from file
   */
  private getPreviewType(file: File): 'image' | 'pdf' | 'text' | 'other' {
    const mimeType = file.type.toLowerCase();
    
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType.startsWith('text/') || mimeType.includes('json')) return 'text';
    
    return 'other';
  }
  
  /**
   * Trigger file selection dialog
   */
  triggerFileSelect(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '*/*';
    
    input.onchange = (e: any) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        this.addFilesToQueue(files);
      }
    };
    
    input.click();
  }
  
  /**
   * Remove current file from queue
   */
  removeCurrentFile(): void {
    if (this.queuedFiles.length === 0) return;
    
    // Revoke object URL
    const current = this.queuedFiles[this.currentIndex];
    if (current.previewUrl && typeof current.previewUrl === 'string') {
      URL.revokeObjectURL(current.previewUrl as any);
    }
    
    this.queuedFiles.splice(this.currentIndex, 1);
    
    if (this.currentIndex >= this.queuedFiles.length) {
      this.currentIndex = Math.max(0, this.queuedFiles.length - 1);
    }
    
    this.initializeFormForCurrentFile();
  }
  
  // ===== NAVIGATION =====
  
  get currentFile(): QueuedFile | null {
    return this.queuedFiles[this.currentIndex] || null;
  }
  
  get canGoPrevious(): boolean {
    return this.currentIndex > 0;
  }
  
  get canGoNext(): boolean {
    return this.currentIndex < this.queuedFiles.length - 1;
  }
  
  goToPrevious(): void {
    if (this.canGoPrevious) {
      this.saveCurrentFormState();
      this.currentIndex--;
      this.initializeFormForCurrentFile();
    }
  }
  
  goToNext(): void {
    if (this.canGoNext) {
      this.saveCurrentFormState();
      this.currentIndex++;
      this.initializeFormForCurrentFile();
    }
  }
  
  goToFile(index: number): void {
    if (index >= 0 && index < this.queuedFiles.length) {
      this.saveCurrentFormState();
      this.currentIndex = index;
      this.initializeFormForCurrentFile();
    }
  }
  
  // ===== FORM MANAGEMENT =====
  
  /**
   * Initialize form values for current file
   */
  private initializeFormForCurrentFile(): void {
    const current = this.currentFile;
    if (!current) {
      this.formValues = {};
      this.selectedDocumentType = null;
      return;
    }
    
    // Restore saved metadata
    this.formValues = { ...current.metadata };
    
    // Restore document type - persist from previous file if not set
    if (current.documentTypeId) {
      this.selectedDocumentType = getDocumentTypeById(current.documentTypeId) || null;
      this.updateFolderCreationFields();
    } else if (this.selectedDocumentType) {
      // Persist document type from previous file
      current.documentTypeId = this.selectedDocumentType.id;
      this.updateFolderCreationFields();
      // Auto-populate fields for the new file
      this.selectedDocumentType.fields.forEach(field => {
        if (field.autoPopulateUserId) {
          this.formValues[field.propertyName] = this.currentUsername;
        }
        if (field.autoPopulateDate) {
          this.formValues[field.propertyName] = new Date().toISOString().split('T')[0];
        }
        if (field.defaultValue && !this.formValues[field.propertyName]) {
          this.formValues[field.propertyName] = field.defaultValue;
        }
      });
    } else {
      this.selectedDocumentType = null;
      this.folderCreationFields = [];
    }
    
    // Restore folder selection - persist from previous file if not set
    if (current.targetFolderId) {
      this.selectTargetFolderById(current.targetFolderId);
    } else if (this.selectedTargetFolder) {
      current.targetFolderId = this.selectedTargetFolder.id;
    }
    
    // Restore folder creation field - persist from previous file if not set
    if (current.createFolderFromField) {
      this.createFolderFromField = current.createFolderFromField;
    } else if (this.createFolderFromField && this.folderCreationFields.length > 0) {
      const isValidField = this.folderCreationFields.some(f => f.propertyName === this.createFolderFromField);
      if (isValidField) {
        current.createFolderFromField = this.createFolderFromField;
      } else {
        this.createFolderFromField = null;
      }
    } else {
      this.createFolderFromField = null;
    }

    if (current.createSubfolderFromField) {
      this.createSubfolderFromField = current.createSubfolderFromField;
    } else if (this.createSubfolderFromField && this.subfolderCreationFields.length > 0) {
      const isValidField = this.subfolderCreationFields.some(f => f.propertyName === this.createSubfolderFromField);
      if (isValidField) {
        current.createSubfolderFromField = this.createSubfolderFromField;
      } else {
        this.createSubfolderFromField = null;
      }
    } else {
      this.createSubfolderFromField = null;
    }
  }
  
  /**
   * Save current form state to queue
   */
  private saveCurrentFormState(): void {
    const current = this.currentFile;
    if (!current) return;
    
    current.metadata = { ...this.formValues };
    current.documentTypeId = this.selectedDocumentType?.id;
    current.targetFolderId = this.selectedTargetFolder?.id;
    current.createFolderFromField = this.createFolderFromField || undefined;
    current.createSubfolderFromField = this.createSubfolderFromField || undefined;
  }
  
  /**
   * Select a document type
   */
  selectDocumentType(docType: DocumentTypeConfig | null): void {
  this.selectedDocumentType = docType;
  this.showDocumentTypeDropdown = false;
  
  // Clear form values when type changes
  this.formValues = {};
  this.formErrors = {};
  
  // Auto-populate user ID fields
  if (docType) {
    docType.fields.forEach(field => {
      if (field.autoPopulateUserId) {
        this.formValues[field.propertyName] = this.currentUsername;
      }
      if (field.autoPopulateDate) {
        this.formValues[field.propertyName] = new Date().toISOString().split('T')[0];
      }
      if (field.defaultValue) {
        this.formValues[field.propertyName] = field.defaultValue;
      }
    });
    
    // Update folder creation options
    this.updateFolderCreationFields();
    this.createFolderFromField = null;
    
    // Update SUBFOLDER creation options (ADD THIS)
    this.updateSubfolderCreationFields();
    this.createSubfolderFromField = null;
    
    // If document type has a base folder, resolve (and create if missing) it and
    // set it as the target — Strike E. Handles plain paths and site paths like
    // /Sites/<site>/documentLibrary/<folder>, so config-driven types don't need
    // the operator to navigate the folder tree.
    // Round 7 — fresh preview state per type; lock applied below when the
    // configured destination resolves (items 7/8).
    this.rulePathPreview = ''; this.rulePathFolio = ''; this.rulePathErrors = [];
    this.destinationLocked = false;
    this.scheduleRulePreview();
    if (docType.baseFolderPath) {
      this.indexingService.resolveOrCreatePath(docType.baseFolderPath).then(folderId => {
        if (folderId) {
          this.selectedTargetFolder = {
            id: folderId,
            name: docType.baseFolderPath!.split('/').filter(Boolean).pop() || 'Base folder',
            path: docType.baseFolderPath!
          };
          // Round 7 — items 7/8: configured destination = locked. The
          // operator never re-picks; "Change location" is the escape hatch.
          this.destinationLocked = true;
          this.saveCurrentFormState();
        }
      }).catch(err => console.warn('Base folder resolve failed:', err));
    }
  } else {
    this.folderCreationFields = [];
    this.createFolderFromField = null;
    this.subfolderCreationFields = [];        // ADD THIS
    this.createSubfolderFromField = null;     // ADD THIS
  }
  
  this.saveCurrentFormState();
}




  
  /**
   * Update available folder creation fields based on selected document type.
   * NOTE: Does NOT reset createFolderFromField — that is handled by 
   * selectDocumentType() (user action) and initializeFormForCurrentFile() (navigation).
   */
  private updateFolderCreationFields(): void {
    if (this.selectedDocumentType) {
      this.folderCreationFields = getFolderCreationFields(this.selectedDocumentType.id);
    } else {
      this.folderCreationFields = [];
    }
    // Do NOT reset this.createFolderFromField here.
    // It was causing the checkbox to clear when navigating between files.
  }
  
  private updateSubfolderCreationFields(): void {
  if (this.selectedDocumentType) {
    this.subfolderCreationFields = getSubfolderCreationFields(this.selectedDocumentType.id);
  } else {
    this.subfolderCreationFields = [];
  }
}

  // ── Round 7: folder-rule engine integration ─────────────────────────
  // When the selected document type defines folderRules, the BACKEND
  // engine decides the structure: live path preview as values are typed,
  // and at save time the resolved segments are created under the (locked)
  // destination. The legacy two-level pickers stay only for types
  // without rules.

  rulePathPreview = '';
  rulePathFolio = '';
  rulePathErrors: string[] = [];
  destinationLocked = false;
  private rulePreviewTimer: ReturnType<typeof setTimeout> | null = null;

  docTypeHasRules(): boolean {
    const r: any = (this.selectedDocumentType as any)?.folderRules;
    return !!(r && Array.isArray(r.levels) && r.levels.length);
  }

  scheduleRulePreview(): void {
    if (!this.docTypeHasRules()) { return; }
    if (this.rulePreviewTimer) { clearTimeout(this.rulePreviewTimer); }
    this.rulePreviewTimer = setTimeout(() => this.refreshRulePreview(), 450);
  }

  private refreshRulePreview(): void {
    const dt: any = this.selectedDocumentType;
    if (!dt || !this.docTypeHasRules()) { return; }
    this.docTypeApi.resolveFolders(dt.typeKey || dt.id, this.formValues, { allocate: false }).subscribe({
      next: r => {
        this.rulePathPreview = r.preview || '';
        this.rulePathFolio = r.folio || '';
        this.rulePathErrors = Object.values(r.errors || {});
      },
      error: () => { /* preview is best-effort */ }
    });
  }

  /**
   * Resolve the folio QNames (aspect + number/folder properties) for the selected document type.
   * These come from the backend (System Settings, per tenant) so filing is tenant-portable —
   * eacc-prefixed by default, skyplus- or bandari-prefixed where the site's content model differs.
   * The eacc fallbacks apply only if the backend omits them; nothing is hardcoded on the write path.
   */
  private async resolveFolioQNames(): Promise<{ numProp: string; folderProp: string; folioAspect: string }> {
    // folioAspect intentionally has NO hardcoded fallback: a BLANK aspect (System Settings
    // `filing.document.aspect` cleared) means "this installation does not use folio" — callers then
    // skip stamping the folio aspect/properties entirely, so no eacc:* (or any) QName is applied.
    // No eacc:* fallbacks anywhere — blank means "not configured" and the caller skips that part.
    const fallback = { numProp: '', folderProp: '', folioAspect: '' };
    const dt: any = this.selectedDocumentType;
    if (!dt) { return fallback; }
    try {
      const r = await this.docTypeApi
        .resolveFolders(dt.typeKey || dt.id, this.formValues, { allocate: false }).toPromise();
      return {
        numProp: (r?.folioNumberProperty || '').trim(),
        folderProp: (r?.folioFolderProperty || '').trim(),
        folioAspect: (r?.folioAspect || '').trim()
      };
    } catch {
      return fallback;
    }
  }

  /** Unlock the auto-resolved destination ("Change location" escape hatch). */
  unlockDestination(): void { this.destinationLocked = false; }

  /**
   * Handle form field change
   */
  onFieldChange(propertyName: string, value: any): void {
    this.formValues[propertyName] = value;
    
    // Clear error for this field
    if (this.formErrors[propertyName]) {
      delete this.formErrors[propertyName];
    }

    // Round 7 — keep the resolved-path preview in step with the values.
    this.scheduleRulePreview();
  }
  
  /**
   * Validate form
   */
  validateForm(): boolean {
    this.formErrors = {};
    
    if (!this.selectedDocumentType) {
      // No document type selected, use default - no validation needed
      return true;
    }
    
    let isValid = true;
    
    this.selectedDocumentType.fields.forEach(field => {
      if (field.mandatory) {
        const value = this.formValues[field.propertyName];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          this.formErrors[field.propertyName] = `${field.label} is required`;
          isValid = false;
        }
      }
    });
    
    return isValid;
  }
  
  // ===== FOLDER BROWSER =====
  
  /**
   * Open folder browser - start from currently selected folder if exists
   */
  openFolderBrowser(): void {
    this.showFolderBrowser = true;
    // Start from the currently selected folder, or root if none
    const startFolderId = this.selectedTargetFolder?.id || '-root-';
    this.loadFolderContents(startFolderId);
  }
  
  /**
   * Close folder browser
   */
  closeFolderBrowser(): void {
    this.showFolderBrowser = false;
  }
  
  /**
   * Load folder contents for browser
   */
  async loadFolderContents(folderId: string): Promise<void> {
    this.isLoading = true;
    this.currentBrowseFolderId = folderId;
    
    try {
      this.browseFolderContents = await this.indexingService.getFolderContents(folderId);
      await this.updateFolderBreadcrumbs(folderId);
    } catch (error) {
      console.error('Error loading folder contents:', error);
      this.browseFolderContents = [];
    } finally {
      this.isLoading = false;
    }
  }
  
  /**
   * Update breadcrumbs for folder browser
   */
  private async updateFolderBreadcrumbs(folderId: string): Promise<void> {
    if (folderId === '-root-') {
      this.folderBreadcrumbs = [{ id: '-root-', name: 'Company Home' }];
      return;
    }
    
    try {
      const node = await this.indexingService.getNodeDetails(folderId);
      if (!node) return;
      const pathElements = node.path?.elements || [];
      
      this.folderBreadcrumbs = [{ id: '-root-', name: 'Company Home' }];
      
      let startIndex = pathElements.findIndex((el: any) => el.name === 'User Homes');
      if (startIndex >= 0) startIndex += 2;
      else startIndex = 0;
      
      for (let i = startIndex; i < pathElements.length; i++) {
        this.folderBreadcrumbs.push({
          id: pathElements[i].id,
          name: pathElements[i].name
        });
      }
      
      this.folderBreadcrumbs.push({ id: node.id, name: node.name });
    } catch (error) {
      console.error('Error loading breadcrumbs:', error);
    }
  }
  
  /**
   * Navigate to folder in browser
   */
  navigateToFolder(folderId: string): void {
    this.loadFolderContents(folderId);
  }
  
  /**
   * Select a folder as target
   */
  selectFolder(folder: { id: string; name: string; path: string }): void {
    this.selectedTargetFolder = folder;
    this.showFolderBrowser = false;
    this.saveCurrentFormState();
  }
  
  /**
   * Select current browse folder as target
   */
  selectCurrentFolder(): void {
    // Build the full path from breadcrumbs
    const fullPath = this.folderBreadcrumbs.map(b => b.name).join(' / ');
    const currentCrumb = this.folderBreadcrumbs[this.folderBreadcrumbs.length - 1];
    
    this.selectedTargetFolder = {
      id: this.currentBrowseFolderId,
      name: currentCrumb?.name || 'Selected Folder',
      path: fullPath
    };
    
    console.log('Selected folder:', this.selectedTargetFolder);  // Debug log
    
    this.showFolderBrowser = false;
    this.saveCurrentFormState();
  }
  
  /**
   * Select folder by ID (for initialization)
   */
  private async selectTargetFolderById(folderId: string): Promise<void> {
    if (folderId === '-root-' || folderId === '-root-') {
      this.selectedTargetFolder = { id: folderId, 
      name: folderId === '-root-' ? 'My Files' : 'Company Home', 
      path: folderId === '-root-' ? 'My Files' : 'Company Home'  };
      return;
    }
    
    try {
      const node = await this.indexingService.getNodeDetails(folderId);
      if (!node) {
        this.selectedTargetFolder = { id: folderId, name: 'Unknown', path: '' };
        return;
      }
      this.selectedTargetFolder = {
        id: node.id,
        name: node.name,
        path: node.path?.name || node.name
      };
    } catch (error) {
      console.error('Error loading folder:', error);
      this.selectedTargetFolder = { id: folderId, name: 'Unknown', path: '' };
    }
  }
  
  // ===== SAVE / UPLOAD =====
  
  /**
   * Strike E — build the ordered list of sub-folder names from the selected
   * type's folder-segment fields (config-driven filing). Returns [] for static
   * types (no folderSegment), so the indexing service falls back to its legacy
   * main + subfolder behaviour and nothing changes for them.
   */
  private buildFolderSegments(): string[] {
    if (!this.selectedDocumentType) { return []; }
    const segFields = this.selectedDocumentType.fields
      .filter(f => f.folderSegment?.enabled)
      .sort((a, b) => (a.folderSegment?.order ?? 0) - (b.folderSegment?.order ?? 0));
    const segments: string[] = [];
    for (const f of segFields) {
      const raw = this.formValues[f.propertyName];
      if (raw === null || raw === undefined || String(raw).trim() === '') { continue; }
      let name = String(raw).trim();
      // For selects, use the option label as the folder name when configured.
      if (f.type === 'select' && f.folderSegment?.useLabel !== false && f.options) {
        const opt = f.options.find(o => o.value === raw);
        if (opt) { name = opt.label; }
      }
      segments.push(name);
    }
    return segments;
  }
  
  /**
   * Save current file (upload with metadata)
   */
  async saveCurrentFile(): Promise<void> {
    if (!this.currentFile) return;
    
    // Validate form
    if (!this.validateForm()) {
      return;
    }
    
    // Ensure folder is selected
    if (!this.selectedTargetFolder) {
      alert('Please select a target folder');
      return;
    }
    
    this.isSaving = true;
    const current = this.currentFile;
    current.status = 'uploading';
    
    try {
      // Debug: Log what folder we're uploading to
      console.log('Uploading to folder:', {
      folderId: this.selectedTargetFolder.id,
      folderName: this.selectedTargetFolder.name,
      folderPath: this.selectedTargetFolder.path,
      createFolder: this.createFolderFromField,
      createFolderValue: this.createFolderFromField ? this.formValues[this.createFolderFromField] : null,
      createSubfolder: this.createSubfolderFromField,                                           // ADD
      createSubfolderValue: this.createSubfolderFromField ? this.formValues[this.createSubfolderFromField] : null  // ADD
    });
    
    // ── Round 7: rules-driven placement ──────────────────────────────
    // The backend engine resolves the segments + folio ONCE (allocate=true),
    // the segments are created idempotently under the destination, and the
    // document uploads straight into the final folder. Legacy two-level
    // logic below never runs for rule-driven types.
    if (this.docTypeHasRules() && this.selectedDocumentType) {
      try {
        const dt: any = this.selectedDocumentType;
        const typeKey: string = dt.typeKey || dt.id;
        const resolved = await this.docTypeApi
          .resolveFolders(typeKey, this.formValues, { allocate: true }).toPromise();
        if (resolved && Object.keys(resolved.errors || {}).length) {
          this.isSaving = false;
          current.status = 'pending';
          alert('Cannot file yet:\n' + Object.values(resolved.errors).join('\n'));
          return;
        }
        const finalFolderId = await this.indexingService.createSegmentPath(
          this.selectedTargetFolder.id, resolved?.segments || []);

        const meta: Record<string, any> = { ...this.formValues };
        const aspects: string[] = [];
        // Folio is a SYSTEM behaviour: when the type's rules enable it the
        // engine allocates the number and we stamp it — no manual aspect
        // setup, nothing typed by the operator. The aspect/property names
        // come from the backend response (System Settings), so this code is
        // tenant-portable: eacc:* here, bandari:* at Bandari, with no change.
        const folioAspect = (resolved?.folioAspect || '').trim();   // blank = folio not used on this install → skip
        if (resolved?.folioNumber != null && folioAspect) {
          const numProp = (resolved.folioNumberProperty || '').trim();     // no eacc:* fallback
          const folderProp = (resolved.folioFolderProperty || '').trim();
          if (numProp) { meta[numProp] = resolved.folioNumber; }
          const segs = resolved.segments || [];
          if (folderProp && segs.length) { meta[folderProp] = segs[segs.length - 1]; }
          aspects.push(folioAspect);
        }

        const result = await this.indexingService.uploadWithMetadata({
          file: current.file,
          documentTypeId: dt.id,
          targetFolderId: finalFolderId,
          metadata: meta,
          autoRename: true
        } as UploadOptions);

        if (result.success) {
          current.status = 'completed';
          current.uploadedNodeId = result.nodeId;
          if (result.nodeId && aspects.length) {
            await this.indexingService.updateNodeMetadata(
              result.nodeId, dt.id, meta, aspects, {});
          }
          const allCompleted = this.queuedFiles.every(f => f.status === 'completed');
          if (allCompleted) {
            alert(`Successfully indexed ${this.queuedFiles.length} document(s)`);
            this.navigateToDocumentLibrary();
          } else if (this.canGoNext) {
            this.goToNext();
          }
        } else {
          current.status = 'error';
          current.error = result.error || 'Upload failed';
          alert(`Upload failed: ${result.error}`);
        }
      } catch (e: any) {
        current.status = 'error';
        current.error = e?.message || 'Upload failed';
        alert(`Upload error: ${e?.message || e}`);
      } finally {
        this.isSaving = false;
      }
      return;
    }

    // Get the subfolder field definition (needed for label lookup)
    let subfolderFieldDef: DocumentField | undefined;
    if (this.createSubfolderFromField && this.selectedDocumentType) {
      subfolderFieldDef = this.selectedDocumentType.fields.find(
        f => f.propertyName === this.createSubfolderFromField
      );
    }
      
      const options: UploadOptions = {
        file: current.file,
        documentTypeId: this.selectedDocumentType?.id,
        targetFolderId: this.selectedTargetFolder.id,
        metadata: this.formValues,
        createFolderFromField: this.createFolderFromField || undefined,
        createSubfolderFromField: this.createSubfolderFromField || undefined,  // ADD THIS
        subfolderFieldDef: subfolderFieldDef, 
        folderSegments: this.buildFolderSegments(),
        autoRename: true
      };
      
      const result = await this.indexingService.uploadWithMetadata(options);
      
      if (result.success) {
        current.status = 'completed';
        current.uploadedNodeId = result.nodeId;

        // Folio: allocate a per-folder folio for the upload's final folder and
        // stamp it, consistent with the manual re-index and hot-folder paths.
        if (result.nodeId && result.folderId && this.selectedDocumentType) {
          try {
            const alloc = await this.filingApi.allocateFolio(result.folderId).toPromise();
            if (alloc && typeof alloc.nextSeq === 'number') {
              // Folio QNames come from the document type / System Settings (tenant-portable:
              // eacc:* by default, skyplus:*/bandari:* where configured) — never hardcoded.
              const { numProp, folderProp, folioAspect } = await this.resolveFolioQNames();
              if (folioAspect) {   // blank = folio not used on this install → skip stamping
                const extraProps: Record<string, any> = {};
                if (numProp) { extraProps[numProp] = alloc.nextSeq; }
                const segs = this.buildFolderSegments();
                if (folderProp && segs.length) { extraProps[folderProp] = segs[segs.length - 1]; }
                await this.indexingService.updateNodeMetadata(
                  result.nodeId, this.selectedDocumentType.id, this.formValues,
                  [folioAspect], extraProps
                );
              }
            }
          } catch (e) {
            console.warn('Folio allocation/stamp after upload failed (document was indexed):', e);
          }
        }
        
        // Check if all files are now completed
        const allCompleted = this.queuedFiles.every(f => f.status === 'completed');
        
        if (allCompleted) {
          // All files saved - show message and navigate back
          alert(`Successfully indexed ${this.queuedFiles.length} document(s)`);
          this.navigateToDocumentLibrary();
        } else if (this.canGoNext) {
          // Auto-advance to next file if available
          this.goToNext();
        }
      } else {
        current.status = 'error';
        current.error = result.error;
        alert(`Upload failed: ${result.error}`);
      }
      
    } catch (error: any) {
      current.status = 'error';
      current.error = error.message;
      alert(`Upload error: ${error.message}`);
    } finally {
      this.isSaving = false;
    }
  }
  
  /**
   * Save all pending files
   */
  async saveAllFiles(): Promise<void> {
    // Validate current form first
    if (!this.validateForm()) {
      return;
    }
    
    this.saveCurrentFormState();
    
    const results: { nodeId: string; folderId: string }[] = [];
    
    for (let i = 0; i < this.queuedFiles.length; i++) {
      const qf = this.queuedFiles[i];
      
      if (qf.status === 'completed') {
        // Already uploaded
        if (qf.uploadedNodeId && qf.targetFolderId) {
          results.push({ nodeId: qf.uploadedNodeId, folderId: qf.targetFolderId });
        }
        continue;
      }
      
      // Navigate to this file
      this.currentIndex = i;
      
      // Upload
      await this.saveCurrentFile();
      
      // Re-read from array since saveCurrentFile modifies the status
      const updatedFile = this.queuedFiles[i];
      if (updatedFile.status === 'completed' && updatedFile.uploadedNodeId && updatedFile.targetFolderId) {
        results.push({ nodeId: updatedFile.uploadedNodeId, folderId: updatedFile.targetFolderId });
      }
    }
    
    // Emit completion event
    if (results.length > 0) {
      this.indexingComplete.emit(results);
    }
    
    // Check if all files are completed successfully
    const allCompleted = this.queuedFiles.every(f => f.status === 'completed');
    if (allCompleted && this.queuedFiles.length > 0) {
      // Show success message and navigate back
      alert(`Successfully indexed ${this.queuedFiles.length} document(s)`);
      this.navigateToDocumentLibrary();
    }
  }
  
  /**
   * Skip to next file without saving
   */
  skipCurrentFile(): void {
    if (this.canGoNext) {
      this.goToNext();
    }
  }
  
  /**
   * Cancel indexing and return to document library
   */
  cancelIndexing(): void {
    if (this.queuedFiles.some(f => f.status === 'pending' || f.status === 'uploading')) {
      if (!confirm('There are unsaved files. Are you sure you want to cancel?')) {
        return;
      }
    }
    
    this.indexingCancelled.emit();
    
    // Navigate back to document library
    this.navigateToDocumentLibrary();
  }
  
  /**
   * Navigate back to document library
   */
  private navigateToDocumentLibrary(): void {
    // Return to the originating folder (?from), else the node's folder, else root.
    const target = this.originFolderId
      || (this.selectedTargetFolder && this.selectedTargetFolder.id !== '-root-'
            ? this.selectedTargetFolder.id : null);
    if (target) {
      this.router.navigate(['/documents', target]);
    } else {
      this.router.navigate(['/documents']);
    }
  }
  
  // ===== EDIT MODE =====
  
  /**
   * Load existing node for editing
   */
  /** Repo path of a node's PARENT folder, from path.elements (Company Home stripped). */
  private nodeParentRepoPath(node: any): string {
    const els: any[] = (node && node.path && node.path.elements) ? node.path.elements : [];
    const segs = els.map((e: any) => e.name).filter((n: string) => n && n !== 'Company Home');
    return '/' + segs.join('/');
  }

  /** Most-specific document type whose base folder path owns the given folder. */
  private matchTypeByFolder(path: string): DocumentTypeConfig | null {
    const p = (path || '').replace(/\/+$/, '');
    let match: DocumentTypeConfig | null = null;
    let bestLen = -1;
    for (const dt of this.documentTypes) {
      const base = (dt.baseFolderPath || '').replace(/\/+$/, '');
      if (!base) { continue; }
      if (p === base || p.startsWith(base + '/')) {
        if (base.length > bestLen) { match = dt; bestLen = base.length; }
      }
    }
    return match;
  }

  async loadNodeForEdit(nodeId: string): Promise<void> {
    this.isEditMode = true;
    this.isLoading = true;
    
    try {
      const node = await this.indexingService.getNodeDetails(nodeId);
      if (!node) {
        alert('Could not load document');
        return;
      }
      
      this.editingNode = node;

      // Fetch the existing content (Bearer auth) so the preview pane shows the
      // actual document instead of a blank icon.
      try {
        const blob = await this.indexingService.getContentAsBlob(nodeId);
        if (blob) {
          const mime = node.content?.mimeType || blob.type || '';
          this.editPreviewType = mime.includes('pdf') ? 'pdf'
            : (mime.startsWith('image/') ? 'image' : 'other');
          this.editPreviewObjectUrl = URL.createObjectURL(blob);
          this.editPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.editPreviewObjectUrl);
        }
      } catch (e) {
        console.warn('Preview fetch failed:', e);
      }
      
      // Determine document type, in priority order:
      //   1) ASPECT match  — an already-indexed doc keeps its real type.
      //   2) FOLDER match  — an unindexed doc defaults to the type that owns
      //      the folder it lives in (parent folder path vs base folder path).
      //   3) NODE TYPE     — last-resort fallback.
      const nodeType = node.nodeType;
      const nodeAspects: string[] = node.aspectNames || [];
      let docType: DocumentTypeConfig | undefined =
        this.documentTypes.find(dt =>
          (dt.aspectNames && dt.aspectNames.some(a => nodeAspects.includes(a))) ||
          (dt.aspectName && nodeAspects.includes(dt.aspectName)));
      if (!docType) {
        docType = this.matchTypeByFolder(this.nodeParentRepoPath(node)) || undefined;
      }
      if (!docType) {
        docType = this.documentTypes.find(dt => dt.nodeType === nodeType);
      }
      
      if (docType) {
        this.selectedDocumentType = docType;
        this.updateFolderCreationFields();
        
        // Load existing property values; for empty fields, apply the same
        // auto-fill rules as capture (current user / date / default) so an
        // unindexed plain-upload still gets its auto-populated fields filled.
        docType.fields.forEach(field => {
          const value = node.properties?.[field.propertyName];
          if (value !== undefined && value !== null && value !== '') {
            this.formValues[field.propertyName] = value;
          } else if (field.autoPopulateUserId) {
            this.formValues[field.propertyName] = this.currentUsername;
          } else if (field.autoPopulateDate) {
            this.formValues[field.propertyName] = new Date().toISOString().split('T')[0];
          } else if (field.defaultValue) {
            this.formValues[field.propertyName] = field.defaultValue;
          }
        });
      }
      
      // Set folder from node path
      if (node.parentId) {
        await this.selectTargetFolderById(node.parentId);
        if (!this.originFolderId) { this.originFolderId = node.parentId; }
      }
      
    } catch (error) {
      console.error('Error loading node for edit:', error);
      alert('Error loading document');
    } finally {
      this.isLoading = false;
    }
  }
  
  /**
   * Save edited metadata
   */
  async saveEditedMetadata(): Promise<void> {
    if (!this.editingNode || !this.selectedDocumentType) return;
    
    if (!this.validateForm()) {
      return;
    }
    
    this.isSaving = true;
    
    try {
      // 1) Resolve the FINAL destination folder — base folder + folder-segment
      //    sub-folders (e.g. .../EACC2_4/PF009501/). Falls back to the document's
      //    current folder when the type has no folder segments.
      const segments = this.buildFolderSegments();
      const basePath = this.selectedDocumentType.baseFolderPath;
      let finalFolderId = this.originFolderId || this.selectedTargetFolder?.id || '';
      let folioFolderName = '';
      if (segments.length > 0 && basePath) {
        try {
          let folderId = await this.indexingService.resolveOrCreatePath(basePath);
          for (const seg of segments) {
            const r = await this.indexingService.getOrCreateFolder(folderId, seg);
            folderId = r.id;
          }
          finalFolderId = folderId;
          folioFolderName = segments[segments.length - 1];
        } catch (e) {
          console.warn('Could not resolve folder segments (will index in place):', e);
        }
      }

      // 2) Allocate a folio number scoped to that destination folder. The
      //    sequence is per-folder, so a different folder gets its own running
      //    folio (atomic server-side allocation).
      const extraProps: Record<string, any> = {};
      const extraAspects: string[] = [];
      // Folio QNames come from the document type / System Settings (tenant-portable:
      // eacc:* by default, skyplus:*/bandari:* where configured) — never hardcoded.
      const { numProp, folderProp, folioAspect } = await this.resolveFolioQNames();
      if (finalFolderId) {
        try {
          const alloc = await this.filingApi.allocateFolio(finalFolderId).toPromise();
          if (alloc && typeof alloc.nextSeq === 'number' && folioAspect) {   // blank aspect = folio off → skip
            if (numProp) { extraProps[numProp] = alloc.nextSeq; }
            if (folderProp && folioFolderName) { extraProps[folderProp] = folioFolderName; }
            extraAspects.push(folioAspect);
          }
        } catch (e) {
          console.warn('Folio allocation failed (continuing without folio):', e);
        }
      }

      // 3) Stamp the type's index metadata + the folio in one update.
      const success = await this.indexingService.updateNodeMetadata(
        this.editingNode.id,
        this.selectedDocumentType.id,
        this.formValues,
        extraAspects,
        extraProps
      );
      
      if (success) {
        // 4) Move the document into the destination folder (if not already there).
        let targetFolderId = finalFolderId || this.selectedTargetFolder?.id || '';
        if (finalFolderId && finalFolderId !== this.originFolderId) {
          try {
            const moved = await this.indexingService.moveNode(this.editingNode.id, finalFolderId);
            if (!moved) { targetFolderId = this.originFolderId || targetFolderId; }
          } catch (e) {
            console.warn('Move into destination folder failed (metadata + folio saved):', e);
          }
        }
        const folio = extraProps[numProp];
        const folioMsg = (folio != null) ? ` (folio #${String(folio).padStart(3, '0')})` : '';
        alert(`Metadata updated successfully${folioMsg}`);
        this.indexingComplete.emit([{
          nodeId: this.editingNode.id,
          folderId: targetFolderId
        }]);
      } else {
        alert('Failed to update metadata');
      }
    } catch (error: any) {
      alert(`Error: ${error.message}`);
    } finally {
      this.isSaving = false;
    }
  }
  
  // ===== HELPERS =====


  
  get completedCount(): number {
    return this.queuedFiles.filter(f => f.status === 'completed').length;
  }
  
  get pendingCount(): number {
    return this.queuedFiles.filter(f => f.status === 'pending').length;
  }
  
  get hasFiles(): boolean {
    return this.queuedFiles.length > 0 || this.isEditMode;
  }
  
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // ===== SUBFOLDER HELPER METHODS (ADD THESE) =====

/**
 * Handle main folder checkbox change
 */
onMainFolderCheckboxChange(checked: boolean): void {
  if (checked && this.folderCreationFields.length > 0) {
    this.createFolderFromField = this.folderCreationFields[0].propertyName;
  } else {
    this.createFolderFromField = null;
    // Also reset subfolder when main folder is unchecked
    this.createSubfolderFromField = null;
  }
}

/**
 * Handle subfolder checkbox change
 */
onSubfolderCheckboxChange(checked: boolean): void {
  if (checked && this.subfolderCreationFields.length > 0) {
    this.createSubfolderFromField = this.subfolderCreationFields[0].propertyName;
  } else {
    this.createSubfolderFromField = null;
  }
}

/**
 * Get subfolder preview name (for display in UI)
 * Uses label for select fields, value for text fields
 */
getSubfolderPreview(field: DocumentField | undefined, value: any): string {
  if (!field || !value) return '';
  
  // For select fields, try to show the label instead of the value
  if (field.type === 'select' && field.options && field.subfolderUseLabel !== false) {
    const option = field.options.find(o => o.value === value);
    if (option) {
      return option.label;
    }
  }
  
  return String(value);
}

/**
 * Get the currently selected subfolder field definition
 */
getSubfolderField(): DocumentField | undefined {
  if (!this.createSubfolderFromField || !this.selectedDocumentType) return undefined;
  return this.selectedDocumentType.fields.find(f => f.propertyName === this.createSubfolderFromField);
}
}